import styled, { css } from "styled-components";
import Breakpoints from "core/constants/breakpoints";
import { fontSizing, lineHeightSizing } from "core/constants/sizing";
import { fontColorSelector } from "core/styles/colorStyle";
import {
  GetMarginStyle,
  getMarginStyle,
  GetPaddingStyle,
  getPaddingStyle,
} from "core/styles/paddingMarginStyle";
import { HTMLProps } from "react";
import { DesignStyle } from "../";

/*
xs: "10px",
s: "12px",
m: "14px",
l: "16px",
xl: "18px",
"h-s": "20px",
"h-m": "24px",
"h-l": "32px",
"h-xl": "48px",
},
*/

type Sizes = "xs" | "s" | "m" | "l" | "xl" | "h-s" | "h-m" | "h-l" | "h-xl";

export type TextType = {
  className?: string;
  width?: string;
  size?: number | Omit<string, Sizes> | Sizes;
  cursor?: string;
  onClick?: any;
  textAlign?: string;
  lineHeight?: string;
  whiteSpace?: string;
  fontStyle?: string;
  fontFamily?: string;
  lineClamp?: number;
  minWidth?: string;
  maxWidth?: string;
  flexShrink?: number;
  textTransform?: string;
  color?: string;
  display?: string;
  backgroundColor?: string;
  borderRadius?: string;
  as?: "button" | "a" | "span" | "h1" | "h2" | "h3" | "div";
} & GetMarginStyle &
  GetPaddingStyle;

const asButtonStyle = ({ as }: Pick<TextType, "as">) => {
  if (as === "button")
    return css`
      outline: none;
      background-color: transparent;
      border: none;
      padding: 0;
      cursor: pointer;
    `;

  if (as === "a")
    return css`
      &:hover {
        cursor: pointer;
        font-family: Nunito-Sans-Bold;
        ${fontColorSelector}
      }
    `;
};

const lineClampStyle = ({ lineClamp = 0 }: { lineClamp?: number }) =>
  lineClamp > 0 &&
  css`
    display: -webkit-box;
    -webkit-line-clamp: ${lineClamp};
    -webkit-box-orient: vertical;
    overflow: hidden;
  `;

export const TextStyled = styled.p<TextType & HTMLProps<HTMLParagraphElement>>`
  font-family: ${({ fontFamily, theme }) =>
    !!fontFamily
      ? fontFamily
      : theme.designStyle === DesignStyle.PROFESSIONAL
      ? "Rubik-Regular"
      : "Nunito-Sans"};
  position: relative;
  width: ${({ width }) => width || "auto"};
  font-size: ${({ size = "m" }) =>
    typeof size === "string" ? fontSizing[Breakpoints.desktop4]?.[size] : `${size || 14}px`};
  margin-bottom: 0px;
  cursor: ${({ cursor, onClick }) => (onClick ? "pointer" : cursor || "inherit")};
  text-align: ${({ textAlign }) => textAlign || "inherit"};
  line-height: ${({ size = "m", lineHeight }) =>
    lineHeight ?? lineHeightSizing[Breakpoints.desktop4]?.[size]};
  white-space: ${({ whiteSpace }) => whiteSpace || "normal"};
  font-style: ${({ fontStyle }) => fontStyle ?? "normal"};
  flex-shrink: ${({ flexShrink }) => flexShrink ?? "unset"};
  text-transform: ${({ textTransform }) => textTransform ?? "unset"};
  background-color: ${({ backgroundColor }) => backgroundColor ?? "none"};
  border-radius: ${({ borderRadius }) => borderRadius};
  min-width: ${({ minWidth }) => minWidth};
  max-width: ${({ maxWidth }) => maxWidth};

  @media screen and (max-width: ${Breakpoints.tablet}px) {
    font-size: ${({ size = "m" }) =>
      typeof size === "string" ? fontSizing[Breakpoints.tablet]?.[size] : `${size || 14}px`};
    line-height: ${({ size = "m", lineHeight }) =>
      lineHeight ?? lineHeightSizing[Breakpoints.tablet]?.[size]};
  }

  @media screen and (max-width: ${Breakpoints.mobile3}px) {
    font-size: ${({ size = "m" }) =>
      typeof size === "string" ? fontSizing[Breakpoints.mobile3]?.[size] : `${size || 14}px`};
    line-height: ${({ size = "m", lineHeight }) =>
      lineHeight ?? lineHeightSizing[Breakpoints.mobile3]?.[size]};
  }

  ${asButtonStyle}
  ${fontColorSelector}
  ${getPaddingStyle}
  ${getMarginStyle}
  ${lineClampStyle};
`;
